import { useState, useEffect, useMemo, Fragment } from 'react';
import { CreditCard, Search, DollarSign, Smartphone, X, Plus, History, Ban, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { ventasApi, busesApi, pagosCreditoApi, creditosPasadosApi } from '../lib/api';
import AnularModal from '../components/AnularModal';

interface Bus {
  padron: string;
  placa: string;
  dueno?: string;
  credito_usado?: number;
  credito_usado_productos?: number;
  credito_usado_servicios?: number;
}

interface CreditoPasado {
  id: string;
  cliente_nombre: string;
  monto_original: number;
  monto_pendiente: number;
  fecha_registro: string;
}

interface VentaPendiente {
  id: string;
  numero: string;
  fecha: string;
  total: number;
  saldo_productos: number;
  saldo_servicios: number;
  items: { nombre: string; cantidad: number; precio_unitario: number; subtotal: number; es_servicio: boolean }[];
}

interface Propietario {
  nombre: string;
  buses: Bus[];
  totalNuevo: number;
  totalAntiguo: number;
  total: number;
}

const METODOS = [
  { valor: 'efectivo', label: 'Efectivo' },
  { valor: 'yape_plin', label: 'Yape / Plin' },
  { valor: 'transferencia', label: 'Transferencia / Depósito' },
  { valor: 'pos', label: 'POS' },
];

export default function Creditos() {
  const [buses, setBuses] = useState<Bus[]>([]);
  const [creditosPasados, setCreditosPasados] = useState<CreditoPasado[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [nombreSeleccionado, setNombreSeleccionado] = useState<string | null>(null);

  const [montoClienteNuevo, setMontoClienteNuevo] = useState('');
  const [creandoClienteNuevo, setCreandoClienteNuevo] = useState(false);
  const [montoNuevaDeudaAntigua, setMontoNuevaDeudaAntigua] = useState('');
  const [montoAbonoAntiguoVentas, setMontoAbonoAntiguoVentas] = useState('');
  const [montoAbonoAntiguoServicios, setMontoAbonoAntiguoServicios] = useState('');
  const [montoPagoNuevo, setMontoPagoNuevo] = useState('');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [observaciones, setObservaciones] = useState('');
  const [pagadoPor, setPagadoPor] = useState('');
  const [capturasYape, setCapturasYape] = useState<string[]>([]);
  const [mostrarCapturaYape, setMostrarCapturaYape] = useState(false);
  const [procesando, setProcesando] = useState<'antigua-nueva' | 'antigua-abono' | 'nuevo-pago' | null>(null);
  const [ultimoResultado, setUltimoResultado] = useState<string | null>(null);
  const [creditoParaAnular, setCreditoParaAnular] = useState<CreditoPasado | null>(null);
  // Cobros de deuda antigua del propietario activo, para anular uno mal registrado (2026-10-23)
  const [pagosAntiguos, setPagosAntiguos] = useState<any[]>([]);
  const [pagoAntiguoParaAnular, setPagoAntiguoParaAnular] = useState<any>(null);
  // Detalle de ventas a credito por bus (se carga al desplegar el bus)
  const [busesAbiertos, setBusesAbiertos] = useState<Record<string, boolean>>({});
  const [ventasPorBus, setVentasPorBus] = useState<Record<string, VentaPendiente[] | 'cargando' | 'error'>>({});
  const [pagosPorBus, setPagosPorBus] = useState<Record<string, any[]>>({});
  const [pagoParaAnular, setPagoParaAnular] = useState<any>(null);
  // Anular una venta a credito desde aqui mismo (2026-10-09, pedido de Milenko): antes
  // solo se podia en Ventas -> Historial. Mismo endpoint, misma clave y motivo.
  const [ventaParaAnular, setVentaParaAnular] = useState<any>(null);

  function alternarBus(padron: string) {
    const abrir = !busesAbiertos[padron];
    setBusesAbiertos((prev) => ({ ...prev, [padron]: abrir }));
    if (abrir) cargarVentasBus(padron);
  }

  function cargarVentasBus(padron: string) {
    setVentasPorBus((prev) => ({ ...prev, [padron]: 'cargando' }));
    pagosCreditoApi.ventasPendientes(padron)
      .then((v) => setVentasPorBus((prev) => ({ ...prev, [padron]: v })))
      .catch(() => setVentasPorBus((prev) => ({ ...prev, [padron]: 'error' })));
    pagosCreditoApi.getByPadron(padron)
      .then((p) => setPagosPorBus((prev) => ({ ...prev, [padron]: p.slice(0, 10) })))
      .catch(() => setPagosPorBus((prev) => ({ ...prev, [padron]: [] })));
  }

  useEffect(() => { cargarTodo(); }, []);

  function cargarTodo() {
    setCargando(true);
    Promise.all([busesApi.getAll(), creditosPasadosApi.getAll()])
      .then(([b, c]) => { setBuses(b); setCreditosPasados(c); setVentasPorBus({}); setPagosPorBus({}); setBusesAbiertos({}); })
      .catch(() => toast.error('No se pudo cargar Créditos'))
      .finally(() => setCargando(false));
  }

  const propietarios = useMemo<Propietario[]>(() => {
    const mapa: Record<string, Propietario> = {};
    buses.forEach((bus) => {
      const key = bus.dueno?.trim();
      if (!key) return;
      if (!mapa[key]) mapa[key] = { nombre: key, buses: [], totalNuevo: 0, totalAntiguo: 0, total: 0 };
      mapa[key].buses.push(bus);
      mapa[key].totalNuevo += parseFloat(String(bus.credito_usado || 0));
    });
    creditosPasados.forEach((c) => {
      const key = c.cliente_nombre.trim();
      if (!mapa[key]) mapa[key] = { nombre: key, buses: [], totalNuevo: 0, totalAntiguo: 0, total: 0 };
      mapa[key].totalAntiguo += parseFloat(String(c.monto_pendiente || 0));
    });
    Object.values(mapa).forEach((p) => { p.total = p.totalNuevo + p.totalAntiguo; });
    return Object.values(mapa).sort((a, b) => b.total - a.total);
  }, [buses, creditosPasados]);

  const propietariosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return propietarios;
    // Busca por nombre del propietario y tambien por padron o placa de cualquiera de sus buses
    // (el personal suele tener el numero de padron, no el nombre exacto del dueno).
    const qPlaca = q.replace(/[^a-z0-9]/g, '');
    return propietarios.filter((p) =>
      p.nombre.toLowerCase().includes(q) ||
      p.buses.some((b) => String(b.padron).toLowerCase().includes(q) ||
        (qPlaca.length >= 3 && String(b.placa || '').toLowerCase().replace(/[^a-z0-9]/g, '').includes(qPlaca))));
  }, [propietarios, busqueda]);

  const propietarioActivo = useMemo(
    () => propietarios.find((p) => p.nombre === nombreSeleccionado) || propietariosFiltrados[0] || null,
    [propietarios, nombreSeleccionado, propietariosFiltrados]
  );

  // Entradas individuales de deuda antigua del propietario activo (una fila por cada
  // vez que se registró deuda) -- necesario para poder anular una puntual, ya que el
  // total de arriba es la suma de todas.
  const creditosDelPropietario = useMemo(
    () => propietarioActivo ? creditosPasados.filter((c) => c.cliente_nombre.trim() === propietarioActivo.nombre) : [],
    [creditosPasados, propietarioActivo]
  );

  useEffect(() => {
    setPagosAntiguos([]);
    if (!propietarioActivo || creditosDelPropietario.length === 0) return;
    let vigente = true;
    creditosPasadosApi.pagos(propietarioActivo.nombre).then((p) => { if (vigente) setPagosAntiguos(p); }).catch(() => {});
    return () => { vigente = false; };
  }, [propietarioActivo?.nombre, creditosPasados]);

  const esYape = metodoPago === 'yape_plin';

  function agregarCapturasYape(files: FileList | null) {
    if (!files) return;
    Array.from(files).forEach((file) => {
      const lector = new FileReader();
      lector.onload = () => setCapturasYape((prev) => [...prev, lector.result as string]);
      lector.readAsDataURL(file);
    });
  }

  async function crearClienteNuevo() {
    const nombre = busqueda.trim();
    const monto = parseFloat(montoClienteNuevo) || 0;
    if (!nombre) return;
    if (monto <= 0) { toast.error('Ingresa un monto mayor a 0'); return; }
    setCreandoClienteNuevo(true);
    try {
      await creditosPasadosApi.crear({ cliente_nombre: nombre, monto });
      toast.success(`Cliente "${nombre}" creado con deuda antigua de S/ ${monto.toFixed(2)}`);
      setMontoClienteNuevo('');
      setNombreSeleccionado(nombre);
      setBusqueda('');
      cargarTodo();
    } catch (e: any) {
      toast.error('Error: ' + e.message);
    } finally {
      setCreandoClienteNuevo(false);
    }
  }

  async function registrarDeudaAntigua() {
    if (!propietarioActivo) return;
    const monto = parseFloat(montoNuevaDeudaAntigua) || 0;
    if (monto <= 0) { toast.error('Ingresa un monto mayor a 0'); return; }
    setProcesando('antigua-nueva');
    try {
      await creditosPasadosApi.crear({ cliente_nombre: propietarioActivo.nombre, monto });
      toast.success(`Deuda antigua de S/ ${monto.toFixed(2)} registrada para ${propietarioActivo.nombre}`);
      setMontoNuevaDeudaAntigua('');
      cargarTodo();
    } catch (e: any) {
      toast.error('Error: ' + e.message);
    } finally {
      setProcesando(null);
    }
  }

  async function abonarDeudaAntigua() {
    if (!propietarioActivo) return;
    const montoVentas = parseFloat(montoAbonoAntiguoVentas) || 0;
    const montoServicios = parseFloat(montoAbonoAntiguoServicios) || 0;
    const monto = montoVentas + montoServicios;
    if (monto <= 0) { toast.error('Ingresa al menos un monto (piezas o servicios)'); return; }
    setProcesando('antigua-abono');
    try {
      const r = await creditosPasadosApi.pagarCliente({
        cliente_nombre: propietarioActivo.nombre, monto_ventas: montoVentas, monto_servicios: montoServicios,
        metodo_pago: metodoPago, observaciones: observaciones.trim() || undefined,
      });
      toast.success(`Abono de S/ ${r.monto_aplicado.toFixed(2)} registrado`);
      setMontoAbonoAntiguoVentas('');
      setMontoAbonoAntiguoServicios('');
      setUltimoResultado(
        `Abono a deuda antigua: S/ ${r.ventas_aplicado.toFixed(2)} → Caja Repuestos · S/ ${r.servicios_aplicado.toFixed(2)} → Caja Servicios`
      );
      cargarTodo();
    } catch (e: any) {
      toast.error('Error: ' + e.message);
    } finally {
      setProcesando(null);
    }
  }

  async function pagarDeudaNueva() {
    if (!propietarioActivo) return;
    const monto = parseFloat(montoPagoNuevo) || 0;
    if (monto <= 0) { toast.error('Ingresa un monto mayor a 0'); return; }
    setProcesando('nuevo-pago');
    try {
      const r = await pagosCreditoApi.pagarPorDueno(propietarioActivo.nombre, {
        monto, metodo_pago: metodoPago, observaciones: observaciones.trim() || undefined, pagado_por: pagadoPor.trim() || undefined,
        yape_capturas: esYape && capturasYape.length ? capturasYape : undefined,
      });
      const detalleBuses = r.buses_tocados.map((b) => `Padrón ${b.padron}`).join(', ');
      toast.success(`Pago de S/ ${r.monto_aplicado.toFixed(2)} registrado (${r.buses_tocados.length} bus${r.buses_tocados.length === 1 ? '' : 'es'})`);
      setUltimoResultado(
        `Repuestos → Caja Repuestos: S/ ${r.productos_aplicado.toFixed(2)} · Servicios → Caja Servicios: S/ ${r.servicios_aplicado.toFixed(2)} · Buses cubiertos (de más antiguo a más reciente): ${detalleBuses}`
      );
      setMontoPagoNuevo('');
      setPagadoPor('');
      setCapturasYape([]);
      cargarTodo();
    } catch (e: any) {
      toast.error('Error: ' + e.message);
    } finally {
      setProcesando(null);
    }
  }

  if (cargando) {
    return <div className="text-center py-12 text-gray-500">Cargando créditos…</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <CreditCard className="text-blue-600" size={24} />
        <h1 className="text-2xl font-bold text-gray-900">Créditos</h1>
        <span className="text-sm text-gray-500 ml-2">Deuda antigua + deuda nueva por propietario, en un solo lugar</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 items-start">
        {/* Panel izquierdo: lista de propietarios */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar propietario, padrón o placa…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="max-h-[600px] overflow-y-auto divide-y">
            {propietariosFiltrados.map((p) => {
              const activo = propietarioActivo?.nombre === p.nombre;
              return (
                <button
                  key={p.nombre}
                  onClick={() => { setNombreSeleccionado(p.nombre); setUltimoResultado(null); }}
                  className={`w-full text-left px-4 py-3 flex justify-between items-center gap-2 ${activo ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{p.nombre}</div>
                    <div className="text-xs text-gray-500 flex gap-2 flex-wrap">
                      {p.buses.length > 0 && <span>{p.buses.length} bus{p.buses.length === 1 ? '' : 'es'}</span>}
                      {p.totalAntiguo > 0 && <span className="text-amber-600">antiguo S/{p.totalAntiguo.toFixed(0)}</span>}
                      {p.totalNuevo > 0 && <span className="text-blue-600">nuevo S/{p.totalNuevo.toFixed(0)}</span>}
                    </div>
                  </div>
                  <div className={`text-sm font-bold whitespace-nowrap ${p.total > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    S/ {p.total.toFixed(2)}
                  </div>
                </button>
              );
            })}
            {propietariosFiltrados.length === 0 && (
              <div className="p-4">
                <p className="text-sm text-gray-400 mb-3">No hay ningún propietario/cliente llamado "{busqueda}".</p>
                <div className="border border-dashed border-gray-300 rounded-lg p-3">
                  <div className="text-xs font-semibold text-gray-600 mb-2">Crear como cliente nuevo con deuda antigua</div>
                  <div className="flex gap-2">
                    <input
                      type="number" min={0} step="0.01" placeholder="Monto"
                      value={montoClienteNuevo}
                      onChange={(e) => setMontoClienteNuevo(e.target.value)}
                      className="flex-1 border rounded px-3 py-1.5 text-sm font-mono"
                    />
                    <button
                      onClick={crearClienteNuevo}
                      disabled={creandoClienteNuevo}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded disabled:opacity-50 whitespace-nowrap"
                    >
                      Crear "{busqueda}"
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho */}
        <div className="bg-white rounded-lg shadow border p-5">
          {!propietarioActivo ? (
            <div className="text-center py-12 text-gray-400">Selecciona un propietario, o búscalo por nombre para registrarle una deuda antigua nueva</div>
          ) : (
            <>
              <div className="flex justify-between items-start mb-4 gap-4 flex-wrap">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{propietarioActivo.nombre}</h2>
                  <p className="text-xs text-gray-500">
                    {propietarioActivo.buses.length > 0 ? `${propietarioActivo.buses.length} buses registrados · ` : ''}
                    deuda antigua S/ {propietarioActivo.totalAntiguo.toFixed(2)} · deuda nueva S/ {propietarioActivo.totalNuevo.toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Deuda total</div>
                  <div className="text-2xl font-bold text-red-600">S/ {propietarioActivo.total.toFixed(2)}</div>
                </div>
              </div>

              {/* Registrar deuda antigua (monto solo, sin descripcion) */}
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 mb-4">
                <div className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                  <Plus size={13} /> Registrar deuda antigua
                </div>
                <p className="text-[11px] text-amber-700 mb-2">Solo el monto, sin descripción — se suma a la deuda antigua de este propietario.</p>
                <div className="flex gap-2">
                  <input
                    type="number" min={0} step="0.01" placeholder="0.00"
                    value={montoNuevaDeudaAntigua}
                    onChange={(e) => setMontoNuevaDeudaAntigua(e.target.value)}
                    className="flex-1 border rounded px-3 py-1.5 text-sm font-mono"
                  />
                  <button
                    onClick={registrarDeudaAntigua}
                    disabled={procesando === 'antigua-nueva'}
                    className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded disabled:opacity-50"
                  >
                    Agregar
                  </button>
                </div>
              </div>

              {/* Detalle de cada entrada de deuda antigua, para poder anular una puntual
                  (ej. se registró por error o duplicada) sin tocar las demás */}
              {creditosDelPropietario.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Detalle de deuda antigua</div>
                  <div className="divide-y border rounded-lg">
                    {creditosDelPropietario.map((c) => (
                      <div key={c.id} className="flex justify-between items-center px-3 py-2 text-sm">
                        <div>
                          <span className="text-gray-500">{new Date(c.fecha_registro).toLocaleDateString('es-PE')}</span>
                          <span className="ml-2 font-mono">
                            S/ {parseFloat(String(c.monto_pendiente)).toFixed(2)}
                            {parseFloat(String(c.monto_pendiente)) !== parseFloat(String(c.monto_original)) && (
                              <span className="text-gray-400"> (de S/ {parseFloat(String(c.monto_original)).toFixed(2)})</span>
                            )}
                          </span>
                        </div>
                        {parseFloat(String(c.monto_pendiente)) > 0 ? (
                          <button
                            onClick={() => setCreditoParaAnular(c)}
                            title="Anular este crédito"
                            className="text-red-600 hover:text-red-800 text-xs underline flex items-center gap-1"
                          >
                            <Ban size={14} /> Anular
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Saldado</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cobros de deuda antigua: se puede anular uno mal registrado (ej. se anotó un monto
                  y el cliente terminó dando otro). Los de antes de que se guardara la caja no se anulan. */}
              {pagosAntiguos.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Pagos de deuda antigua</div>
                  <div className="divide-y border rounded-lg max-h-64 overflow-y-auto">
                    {pagosAntiguos.map((p, i) => (
                      <div key={p.caja_chica_id || i} className="flex justify-between items-center px-3 py-2 text-sm gap-2">
                        <div>
                          <span className="text-gray-500">{new Date(p.fecha).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          <span className="ml-2 font-mono font-semibold">S/ {Number(p.monto).toFixed(2)}</span>
                          {p.comprobantes?.length > 0 && <span className="ml-2 text-xs text-gray-400">{p.comprobantes.join(', ')}</span>}
                        </div>
                        {p.anulable && (
                          <button
                            onClick={() => setPagoAntiguoParaAnular(p)}
                            title="Anular este pago"
                            className="text-red-600 hover:text-red-800 text-xs underline flex items-center gap-1 shrink-0"
                          >
                            <Ban size={14} /> Anular pago
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabla de buses (deuda nueva, solo lectura) */}
              {propietarioActivo.buses.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <History size={13} /> Buses (deuda nueva, con detalle)
                  </div>
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold text-left">
                          <th className="px-2 py-1.5">Bus</th>
                          <th className="px-2 py-1.5 text-right">Repuestos</th>
                          <th className="px-2 py-1.5 text-right">Servicios</th>
                          <th className="px-2 py-1.5 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {propietarioActivo.buses.map((bus) => {
                          const deudaProd = parseFloat(String(bus.credito_usado_productos || 0));
                          const deudaServ = parseFloat(String(bus.credito_usado_servicios || 0));
                          const total = parseFloat(String(bus.credito_usado || 0));
                          const sinDeuda = total <= 0;
                          return (
                            <Fragment key={bus.padron}>
                            <tr className={`cursor-pointer hover:bg-gray-50 ${sinDeuda ? 'text-gray-400' : ''}`} onClick={() => alternarBus(bus.padron)}>
                              <td className="px-2 py-1.5">
                                {(busesAbiertos[bus.padron]
                                  ? <ChevronDown size={14} className="inline -mt-0.5 mr-1 text-gray-400" />
                                  : <ChevronRight size={14} className="inline -mt-0.5 mr-1 text-gray-400" />)}
                                <span className="font-medium text-gray-900">Padrón {bus.padron}</span>
                                {bus.placa && bus.placa !== '—' && <span className="text-gray-400 text-xs ml-1">{bus.placa}</span>}
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">{deudaProd > 0 ? `S/ ${deudaProd.toFixed(2)}` : '—'}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{deudaServ > 0 ? `S/ ${deudaServ.toFixed(2)}` : '—'}</td>
                              <td className={`px-2 py-1.5 text-right font-mono font-semibold ${total > 0 ? 'text-red-600' : ''}`}>{total > 0 ? `S/ ${total.toFixed(2)}` : '—'}</td>
                            </tr>
                            {busesAbiertos[bus.padron] && (
                              <tr className="bg-gray-50">
                                <td colSpan={4} className="px-3 py-2">
                                  {(() => {
                                    const v = ventasPorBus[bus.padron];
                                    if (!v || v === 'cargando') return <div className="text-xs text-gray-400">Cargando ventas…</div>;
                                    if (v === 'error') return <div className="text-xs text-red-600">No se pudo cargar el detalle</div>;
                                    if (v.length === 0) return <div className="text-xs text-gray-500">Sin ventas a crédito pendientes.</div>;
                                    return (
                                      <div className="space-y-2">
                                        {v.map((venta) => {
                                          const total = parseFloat(String(venta.total));
                                          const debe = parseFloat(String(venta.saldo_productos)) + parseFloat(String(venta.saldo_servicios));
                                          return (
                                            <div key={venta.id} className="border-l-2 border-red-200 pl-2">
                                              <div className="flex justify-between text-xs">
                                                <span>
                                                  <span className="text-gray-500">{new Date(venta.fecha).toLocaleDateString('es-PE')}</span>
                                                  <span className="ml-2 font-semibold text-gray-700">{venta.numero}</span>
                                                </span>
                                                <span className="font-mono flex items-center gap-2">
                                                  <span>
                                                    S/ {total.toFixed(2)}
                                                    {Math.abs(debe - total) > 0.004 && <span className="text-red-600"> · debe S/ {debe.toFixed(2)}</span>}
                                                  </span>
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); setVentaParaAnular({ ...venta, padron: bus.padron, total, debe }); }}
                                                    className="inline-flex items-center gap-0.5 text-[11px] text-red-600 hover:text-red-800 font-sans"
                                                    title="Anular esta venta (pedido por error)"
                                                  >
                                                    <Ban size={12} /> Anular
                                                  </button>
                                                </span>
                                              </div>
                                              <ul className="mt-0.5 text-[11px] text-gray-600">
                                                {venta.items.map((it, i) => (
                                                  <li key={i} className="flex justify-between gap-2">
                                                    <span>{parseFloat(String(it.cantidad))} × {it.nombre}{it.es_servicio && <span className="text-blue-600"> (servicio)</span>}</span>
                                                    <span className="font-mono whitespace-nowrap">S/ {parseFloat(String(it.subtotal)).toFixed(2)}</span>
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                  {(pagosPorBus[bus.padron] || []).length > 0 && (
                                    <div className="mt-3">
                                      <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Pagos registrados (últimos 10)</div>
                                      {(pagosPorBus[bus.padron] || []).map((p) => (
                                        <div key={p.id} className="flex justify-between items-center text-xs py-0.5">
                                          <span>
                                            <span className="text-gray-500">{new Date(p.fecha).toLocaleDateString('es-PE')}</span>
                                            <span className="ml-2 font-mono text-green-700">S/ {parseFloat(p.monto).toFixed(2)}</span>
                                            <span className="ml-2 text-gray-400">{p.metodo_pago}</span>
                                            {p.pagado_por && <span className="ml-2 text-gray-600">· pagó: {p.pagado_por}</span>}
                                          </span>
                                          {Array.isArray(p.detalle_ventas) ? (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); setPagoParaAnular({ ...p, padron: bus.padron }); }}
                                              className="text-red-600 hover:text-red-800 underline flex items-center gap-1"
                                            >
                                              <Ban size={12} /> Anular
                                            </button>
                                          ) : (
                                            <span className="text-gray-300" title="Pago registrado antes del 06/10/2026: no se puede anular desde aquí">—</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Metodo de pago + observaciones + yape, compartido por las 2 acciones de pago */}
              {(propietarioActivo.totalAntiguo > 0 || propietarioActivo.totalNuevo > 0) && (
                <div className="border-t pt-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Método de pago</label>
                      <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                        {METODOS.map((m) => <option key={m.valor} value={m.valor}>{m.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Observaciones (opcional)</label>
                      <input
                        type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium mb-1">¿Quién trae el dinero? <span className="text-gray-400 font-normal">(opcional, si no es el propietario — ej. el chofer)</span></label>
                      <input
                        type="text" value={pagadoPor} onChange={(e) => setPagadoPor(e.target.value)} maxLength={80}
                        placeholder="Nombre de quien paga" className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  {esYape && (
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-semibold text-purple-800">Comprobante de Yape (opcional, aplica al pago de deuda nueva)</h4>
                        <button type="button" onClick={() => setMostrarCapturaYape(true)} className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                          + Agregar captura
                        </button>
                      </div>
                      {capturasYape.length > 0 && (
                        <div className="grid grid-cols-6 gap-2">
                          {capturasYape.map((c, i) => (
                            <div key={i} className="relative">
                              <img src={c} alt={`Captura ${i + 1}`} className="w-full h-12 object-cover rounded border" />
                              <button type="button" onClick={() => setCapturasYape((prev) => prev.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                                <X size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {propietarioActivo.totalAntiguo > 0 && (
                      <div className="border border-gray-200 rounded-lg p-3">
                        <div className="text-xs font-semibold text-gray-700 mb-1">Abonar deuda antigua</div>
                        <div className="text-[11px] text-gray-400 mb-2">Pendiente: S/ {propietarioActivo.totalAntiguo.toFixed(2)} · reparte el cobro entre las dos cajas</div>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="text-[10px] text-gray-500">Piezas → Caja Repuestos</label>
                            <input
                              type="number" min={0} step="0.01" placeholder="0.00"
                              value={montoAbonoAntiguoVentas} onChange={(e) => setMontoAbonoAntiguoVentas(e.target.value)}
                              className="w-full border rounded px-3 py-1.5 text-sm font-mono"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500">Servicios → Caja Servicios</label>
                            <input
                              type="number" min={0} step="0.01" placeholder="0.00"
                              value={montoAbonoAntiguoServicios} onChange={(e) => setMontoAbonoAntiguoServicios(e.target.value)}
                              className="w-full border rounded px-3 py-1.5 text-sm font-mono"
                            />
                          </div>
                        </div>
                        <button
                          onClick={abonarDeudaAntigua}
                          disabled={procesando === 'antigua-abono'}
                          className="w-full px-4 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold rounded disabled:opacity-50"
                        >
                          Abonar
                        </button>
                      </div>
                    )}
                    {propietarioActivo.totalNuevo > 0 && (
                      <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                        <div className="text-xs font-semibold text-blue-800 mb-1">Pagar deuda nueva</div>
                        <div className="text-[11px] text-blue-600 mb-2">Pendiente: S/ {propietarioActivo.totalNuevo.toFixed(2)} · reparto automático: repuestos primero, más antigua primero</div>
                        <div className="flex gap-2">
                          <input
                            type="number" min={0} max={propietarioActivo.totalNuevo} step="0.01" placeholder="0.00"
                            value={montoPagoNuevo} onChange={(e) => setMontoPagoNuevo(e.target.value)}
                            className="flex-1 border rounded px-3 py-1.5 text-sm font-mono"
                          />
                          <button
                            onClick={pagarDeudaNueva}
                            disabled={procesando === 'nuevo-pago'}
                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded disabled:opacity-50 flex items-center gap-1"
                          >
                            <DollarSign size={14} /> Pagar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {ultimoResultado && (
                    <div className="text-xs bg-green-50 border border-green-200 text-green-800 rounded-lg p-3">
                      {ultimoResultado}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {mostrarCapturaYape && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-purple-600" />
                Capturas de comprobante Yape
              </h3>
              <button onClick={() => setMostrarCapturaYape(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg py-8 cursor-pointer hover:bg-purple-50">
              <Smartphone className="w-8 h-8 text-purple-400 mb-2" />
              <span className="text-sm text-purple-700 font-medium">Click para elegir captura(s) de pantalla</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { agregarCapturasYape(e.target.files); e.target.value = ''; }} />
            </label>
            {capturasYape.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {capturasYape.map((c, i) => (
                  <div key={i} className="relative">
                    <img src={c} alt={`Captura ${i + 1}`} className="w-full h-24 object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => setCapturasYape((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setMostrarCapturaYape(false)} className="w-full mt-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              Listo ({capturasYape.length} captura{capturasYape.length === 1 ? '' : 's'})
            </button>
          </div>
        </div>
      )}

      {pagoParaAnular && (
        <AnularModal
          titulo="Anular pago de crédito"
          descripcion={`Se anulará el pago de S/ ${parseFloat(pagoParaAnular.monto).toFixed(2)} del bus ${pagoParaAnular.padron} (${new Date(pagoParaAnular.fecha).toLocaleDateString('es-PE')}). La deuda vuelve a las ventas que cubría y el ingreso sale de caja. Después registra el pago correcto.`}
          textoBoton="Anular pago"
          onClose={() => setPagoParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            await pagosCreditoApi.anular(pagoParaAnular.id, { clave, motivo });
            toast.success('Pago anulado');
            setPagoParaAnular(null);
            cargarTodo();
          }}
        />
      )}

      {ventaParaAnular && (
        <AnularModal
          titulo={`Anular venta ${ventaParaAnular.numero} — Padrón ${ventaParaAnular.padron}`}
          descripcion={Math.abs(ventaParaAnular.debe - ventaParaAnular.total) > 0.004
            ? `Esta venta ya tiene pagos aplicados (debe S/ ${ventaParaAnular.debe.toFixed(2)} de S/ ${ventaParaAnular.total.toFixed(2)}). Primero anula esos pagos en "Pagos registrados" de este bus y luego anula la venta.`
            : `Se anulará la venta de S/ ${ventaParaAnular.total.toFixed(2)} del ${new Date(ventaParaAnular.fecha).toLocaleDateString('es-PE')}: se quita esa deuda al bus y los productos vuelven al stock. Queda registrada en Auditoría con el motivo.`}
          textoBoton="Anular venta"
          onClose={() => setVentaParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            await ventasApi.anular(ventaParaAnular.id, { clave, motivo });
            toast.success(`Venta ${ventaParaAnular.numero} anulada`);
            setVentaParaAnular(null);
            cargarTodo();
          }}
        />
      )}

      {pagoAntiguoParaAnular && (
        <AnularModal
          titulo={`Anular pago de deuda antigua — ${propietarioActivo?.nombre || ''}`}
          descripcion={`Se anulará el pago de S/ ${Number(pagoAntiguoParaAnular.monto).toFixed(2)} del ${new Date(pagoAntiguoParaAnular.fecha).toLocaleString('es-PE')}: la plata sale de la caja${pagoAntiguoParaAnular.monto_servicios > 0 && pagoAntiguoParaAnular.monto_ventas > 0 ? ' (Repuestos y Servicios)' : ''} y esa deuda vuelve al cliente. Si pagó otro monto, regístralo después con "Abonar deuda antigua".`}
          textoBoton="Anular pago"
          onClose={() => setPagoAntiguoParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            await creditosPasadosApi.anularPago(pagoAntiguoParaAnular.caja_chica_id, { clave, motivo });
            toast.success('Pago anulado');
            setPagoAntiguoParaAnular(null);
            cargarTodo();
          }}
        />
      )}

      {creditoParaAnular && (
        <AnularModal
          titulo={`Anular crédito — ${creditoParaAnular.cliente_nombre}`}
          descripcion={`Se anulará el saldo pendiente de S/ ${parseFloat(String(creditoParaAnular.monto_pendiente)).toFixed(2)} de este crédito antiguo (registrado el ${new Date(creditoParaAnular.fecha_registro).toLocaleDateString('es-PE')}).`}
          textoBoton="Anular crédito"
          onClose={() => setCreditoParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            await creditosPasadosApi.anular(creditoParaAnular.id, { clave, motivo });
            toast.success('Crédito anulado');
            setCreditoParaAnular(null);
            cargarTodo();
          }}
        />
      )}
    </div>
  );
}
